/** @format */

import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { Modal, Button, Form, Alert } from 'react-bootstrap';
import ButtonSyncLoader from 'components/ButtonSyncLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

import './styles.scss';

const Switch = Form.Switch;

const MIN_PASSWORD_LEGTH = 8;
const ValidationErrors = {
  PasswordPatternError: 'PasswordPatternError'
};

export default function ResetPasswordModal({
  showModal,
  handleClose,
  brokerData,
  handleReset
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [requestPending, setRequestPending] = useState(false);
  const [isRequestSuccess, setIsRequestSuccess] = useState(false);

  const clearStates = () => {
    setShowPassword(false);
    setRequestPending(false);
    setIsRequestSuccess(false);

    formik.resetForm();
  };

  useEffect(() => {
    if (showModal) {
      clearStates();
    }
  }, [showModal]);

  const formikValidationSchema = () =>
    Yup.object().shape({
      newPassword: Yup.string()
        .required('Please type the password')
        .min(
          MIN_PASSWORD_LEGTH,
          `Password should have at least ${MIN_PASSWORD_LEGTH} characters`
        )
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$#!%*?&])[A-Za-z\d@$#!%*?&]+$/,
          ValidationErrors.PasswordPatternError
        )
    });

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      permanent: false
    },
    onSubmit: async (values) => {
      if (values.newPassword) {
        try {
          setRequestPending(true);
          await handleReset({
            username: brokerData.username,
            password: values.newPassword,
            isPermanent: values?.permanent || false
          });
          setIsRequestSuccess(true);
          setTimeout(() => {
            clearStates();
            handleClose();
          }, 3000);
        } catch (error) {
          formik.setErrors({
            newPassword: 'Error occurred, Please try again'
          });
        } finally {
          setRequestPending(false);
        }
      }
    },
    validationSchema: formikValidationSchema,
    validateOnChange: false,
    validateOnBlur: false
  });

  const PasswordVisiblityToggle = () => {
    return (
      <FontAwesomeIcon
        icon={showPassword ? faEyeSlash : faEye}
        size={'lg'}
        onClick={() => setShowPassword(!showPassword)}
      />
    );
  };

  const PasswordError = ({ errorMessage }) => {
    switch (errorMessage) {
      case ValidationErrors.PasswordPatternError:
        return (
          <div className='password-pattern-error'>
            <span>Password should have </span>
            <br />
            <ul>
              <li>Lowercase characters</li>
              <li>Uppercase characters</li>
              <li>Numbers</li>
              <li>$%&@!# symbols</li>
            </ul>
          </div>
        );

      default:
        return <span className='validation-error'>{errorMessage}</span>;
    }
  };

  const PermanentPasswordWarning = () => (
    <Alert variant='info'>
      Please note, when you set the password as permanent, the broker will NOT
      be asked to change the password on the first login
    </Alert>
  );

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      backdrop='static'
      keyboard={false}
      size='lg'
      className='reset-password-modal'>
      <form
        onSubmit={(event) => {
          formik.handleSubmit(event);
        }}>
        <Modal.Header closeButton>
          <Modal.Title>
            <p className='text1 pink-text'>Set New Password</p>
            {brokerData?.firstName && brokerData?.lastName && (
              <p className='text1 pink-text'>
                for {`${brokerData.firstName} ${brokerData.lastName}`}
              </p>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='reset-password-modal-body-content'>
            <span className='text3m'>New password</span>
            <div className='form-item'>
              <input
                name='newPassword'
                className='password-input'
                type={showPassword ? 'text' : 'password'}
                placeholder='Type here'
                value={formik.values.newPassword}
                onChange={formik.handleChange}
              />
              <PasswordVisiblityToggle />
            </div>
            <PasswordError errorMessage={formik.errors?.newPassword} />

            <div className='form-item permanent-switch-wrapper'>
              <span className='text3m'>Permanent</span>
              <Switch
                name='permanent'
                value={formik.values.permanent}
                onChange={formik.handleChange}
              />
              <span className='text3m result'>
                {formik.values.permanent ? 'Yes' : 'No'}
              </span>
            </div>
            {formik.values.permanent && <PermanentPasswordWarning />}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='modal-button-container'>
            {!isRequestSuccess && (
              <Button
                type='submit'
                className='btn btn-pink modal-fullwidth-button'
                disabled={requestPending}>
                {requestPending ? <ButtonSyncLoader /> : 'Set new password'}
              </Button>
            )}
            {isRequestSuccess && (
              <Button
                type='button'
                className='btn btn-success modal-fullwidth-button success-message'
                disabled={true}>
                Password updated successfully
              </Button>
            )}
            <Button
              className='btn btn-white modal-fullwidth-button modal-second-button'
              onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
